import * as Safe from "safe-portals";
import { CampaignProviderConfig, CampaignProviderConfigSerializer } from "./campaign_provider";
export interface RawPersonalData {
  owner: string[];
  data: unknown;
  consents: Consent[];
}

export interface PersonalData<T> {
  owner: string[];
  data: T;
  consents: Consent[];
}

export interface RawAuditLog {
  personal_data_guid: string;
  url: string;
  action: string;
}

// type aliases for clarity
export type JourneyGuid = string;
export type ActionGuid = string;
export type ActionType = string;

export interface Member {
  // used by crm.getMember
  external_id?: string;
  name?: string;
  postcode?: string;
  email?: string;
  phone?: string;
  line1?: string;
  town?: string;
}

/**
 * A journey context represents a members interactions with a journey as they progress through,
 * e.g. what we know about a member, the data collected by other actions"
 */
export interface JourneyContext {
  member: Member;
  actionData: any;
  utm: {
    source?: string;
    medium?: string;
    campaign?: string;
  };
  seed: number;
}

export interface ConditionInputDefinition {
  internal_name: string;
  label: string;
  fn: ConditionInput;
}

/*
 * A supplier of condition values to a branch condition.
 *
 * Eg.. takes context.member.postcode, uses external API to return
 * parliamentary constituency code.
 */
export type ConditionInput = (comparison: BranchComparison, context: JourneyContext) => Promise<any>;
export type BranchComparison = {
  op: "truthy" | "falsey";
  input: string; // ConditionInput unique id
  args: any;
};

export interface BranchCondition {
  target_guid: ActionGuid;
  comparison: BranchComparison | undefined;
}

export interface Action {
  guid: ActionGuid;
  journey_guid: JourneyGuid;
  /**
   * null is for pure conditional action nodes - the user does not interact or take action on these steps, but flow
   * decides what the next action they will interact with will be depending on the BranchConditions
   */
  type: ActionType | null;
  config: any[];
  branches: BranchCondition[];
  winning_variant_idx: number | undefined; // index into config[] variants
}

export type ActionSummary = Omit<Action & { successful_actions: number }, "config" | "branches">;

/**
 * Represents the collection of Actions that form a single Journey.
 */
export interface Journey {
  guid: JourneyGuid;
  slug: string;
  name: string;
  internal_name: string | undefined;
  created_at: Date;
  updated_at: Date;
  actions: Action[];
  went_live_at: Date | undefined;
  og_image: string;
  og_description: string;
  og_title: string;
  initial_action_guid: string | undefined;
  created_by: string;
  show_journey_title: boolean; // whether the journey level title is shown on each action
  show_on_public_lists: boolean; // whether the journey is shown on the homepage - used by the wordpress frontend
  /** campaigns associated with this journey */
  campaigns: CampaignProviderConfig | undefined;
}

export type Donation = Safe.TypeIn<typeof donation>;
export type DonationMetadata = Safe.TypeIn<typeof donationMetadataSerializer>;
export type DonationFrequency = Safe.TypeIn<typeof donationFrequency>;
export type DonationReport = Safe.TypeIn<typeof donationReport>;
export type JourneyListItem = Safe.TypeIn<typeof Serializers.journeyListItem>;
export type JourneyListPublicItem = Safe.TypeIn<typeof Serializers.journeyListPublicItem>;

// Personal data consents structures
export type ConsentLevel = Safe.TypeIn<typeof Serializers.consentLevel>;
export type ConsentInput = Safe.TypeIn<typeof Serializers.consentInput>;
export type ConsentConfig = Safe.TypeIn<typeof Serializers.consentConfig>;
export type ConsentText = Safe.TypeIn<typeof Serializers.consentText>;
export type Consent = Safe.TypeIn<typeof Serializers.consent>;

// types to be pushed to CRM systems
export type CrmSurveyResponse = Safe.TypeIn<typeof Serializers.crmSurveyResponse>;
export type CrmDonation = Safe.TypeIn<typeof Serializers.crmDonation>;

export type CampaignTarget = Safe.TypeIn<typeof Serializers.campaignTarget>;
export type PublicCampaignTarget = Safe.TypeIn<typeof Serializers.publicCampaignTarget>;
export type CampaignTargetUniqueId = string; // unique per campaign target provider

export type Campaign = Safe.TypeIn<typeof Serializers.campaign>;

// like Action type, but hiding the `config` data
export type PublicAction = Safe.TypeIn<typeof Serializers.publicAction>;
export type JourneyActionStats = Safe.TypeIn<typeof Serializers.journeyActionStats>;
export type PublicJourneyStep = Safe.TypeIn<typeof publicJourneyStep>;
export type PublicJourneyStepResponse = Safe.TypeIn<typeof publicJourneyStepResponse>;

export type JourneyTemplate = Safe.TypeIn<typeof Serializers.journeyTemplate>;
export type JourneyTemplates = Safe.TypeIn<typeof Serializers.journeyTemplates>;
export type JourneyContinuation = Safe.TypeIn<typeof Serializers.journeyContinuation>;

// used to inform the member frontend of what member data the server holds, without disclosing too much
export type PublicPrefillData = Safe.TypeIn<typeof publicPrefillData>;
export type SamPrefillData = Safe.TypeIn<typeof samPrefillData>;
export type User = Safe.TypeIn<typeof user>;
export type Role = Safe.TypeIn<typeof role>;
export type FilestoreStats = Safe.TypeIn<typeof filestoreStats>;
export type FilestoreFile = Safe.TypeIn<typeof filestoreFile>;
// documentary type
export type FilestoreGuid = string;
export type EmailAddress = Safe.TypeIn<typeof emailAddress>;

const donationFrequency = Safe.oneOf("once", "weekly", "monthly", "yearly");

const donationReport = Safe.obj({
  currency: Safe.str,
  frequency: donationFrequency,
  // This actually is the 'total_amount' of donations for the associated Journey.
  amount: Safe.float,
  // The same as above, but more clearly named.
  total_base_amount: Safe.float,
  // The total amount of boosted parts of donations for the associated Journey.
  total_boost_amount: Safe.float,
  // The total number of donations for the associated Journey that were boosted (have a boost amount).
  num_boosted: Safe.int,
  // The total number of donations for the associated Journey.
  num_donations: Safe.int,
});

// It is a pity this resembles duplicating the type definitions above.
// A language like Rust or Haskell would allow this to be generic.
// On the plus side, it is at least statically validated.
const campaignTarget = Safe.obj({
  unique_id: Safe.str, // unique to a given target provider
  name: Safe.str,
  email: Safe.str,
  phone: Safe.str,
  twitter: Safe.str,
  facebook: Safe.str,
  address: Safe.str,
  role: Safe.optional(Safe.str),
});

// A campaign is an organisation structure for actions.
// A campaign might be like "NHS Payrise" or "COP-26"
// Flow doesn't really do anything with campaigns at the moment, but
// CRMS such as Identity use them to group actions and link them to issues.
// Putting the concept in flow is a workflow optimisation for now, saving campaigners
// from having to move between loads of different apps
//
// The id is the id in the external system, i.e. Identity
// Name is displayed to admins, and maybe at some point members
const campaign = Safe.obj({
  id: Safe.str,
  name: Safe.str,
});

// This type hides most of the campaign target data (since it is available
// to the public frontend), but includes an opaque 'unique_id',
// which identifies the complete CampaignTarget data for the configured
// target provider
const publicCampaignTarget = Safe.obj({
  name: Safe.str,
  unique_id: Safe.str,
});

const consentLevel = Safe.oneOf("no_change", "none_given", "implicit", "explicit_opt_in");

export type ConsentLevelType = Safe.TypeIn<typeof consentLevel>;

// The possible options of consentInput.
// Tracked on Consent and sent to Identity/CRM
// SW-200 - New 'onfieldpresent' type added. Trying to follow existing patern w.r.t naming.
//          Denotes a mechanism by which a consent is given by the presence of a field in a form.
const consentMethod = Safe.oneOf("implicit", "onfieldpresent", "checkbox", "radiobuttons");

const consentInput = Safe.variant(
  "implicit",
  Safe.obj({}),

  // SW-200 - New 'onfieldpresent' type added. Currently, is be using for the email field in the form.
  // would required sub-discrimination if other variant are added in the future..
  "onfieldpresent",
  Safe.obj({}),

  "checkbox",
  Safe.obj({
    consent_label: Safe.str,
    consent_help: Safe.str,
    required: Safe.bool,
    mapping: Safe.tuple(consentLevel, consentLevel),
  }),

  "radiobuttons",
  Safe.obj({
    consent_yes_label: Safe.str,
    consent_no_label: Safe.str,
    yes_consent_level: consentLevel, // consent level that 'yes' maps to
    no_consent_level: consentLevel, // consent level that 'no' maps to
    required_msg: Safe.optional(Safe.str),
    nag_msg: Safe.optional(Safe.str),
  })
);

/**
 * A consent text only represents part of what we need to know to collect consents from the user,
 * it does not provide us with info about the input mechanism to uses (e.g. radio buttons),
 * the option text to show (e.g. 'I agree'), when to show the consent (e.g. always), etc.
 * Within Flow components use a consentConfig object rather than using a consent text directly so all the
 * required info is in one place.
 */
const consentConfig = Safe.obj({
  guid: Safe.str,
  name: Safe.str,
  input: consentInput,
  updated_at: Safe.dateUnixMillis,
  always_show: Safe.bool,
  // if this is present it will ovveride the text pulled from the consent text object
  short_text_override: Safe.optional(Safe.str),

  // these below are read only values pulled from the associated consent text object
  consent_text_public_id: Safe.str,
  consent_text_short_text: Safe.str,
  consent_text_full_legal_text_link: Safe.str,
});

const consentText = Safe.obj({
  public_id: Safe.str,
  consent_short_text: Safe.str,
  full_legal_text_link: Safe.str,
  created_at: Safe.dateIso,
  updated_at: Safe.dateIso,
});

/**
 * Represents a users response to being asked to give a consent e.g.
 * ('privacy-policy-17-08-2021' , 'explicit_opt_in')
 */
const consent = Safe.obj({
  public_id: Safe.str,
  consent_level: consentLevel,
  consent_method: Safe.optional(consentMethod),
  consent_method_option: Safe.optional(Safe.str),
});

const personalDataOwner = Safe.array(Safe.str);

const branchCondition: Safe.Type<BranchCondition> = Safe.obj({
  target_guid: Safe.str,
  comparison: Safe.optional(
    Safe.obj({
      op: Safe.oneOf("truthy", "falsey"),
      input: Safe.str,
      args: Safe.raw,
    })
  ),
});

const member: Safe.Type<Member> = Safe.partial_obj({
  external_id: Safe.str,
  name: Safe.str,
  postcode: Safe.str,
  email: Safe.str,
  phone: Safe.str,
  line1: Safe.str,
  town: Safe.str,
});

const journeyContext: Safe.Type<JourneyContext> = Safe.obj({
  member: member,
  actionData: Safe.raw,
  utm: Safe.partial_obj({
    source: Safe.str,
    medium: Safe.str,
    campaign: Safe.str,
  }),
  seed: Safe.int,
});

const action: Safe.Type<Action> = Safe.obj({
  guid: Safe.str,
  journey_guid: Safe.str,
  type: Safe.nullable(Safe.str),
  config: Safe.array(Safe.raw),
  branches: Safe.array(branchCondition),
  winning_variant_idx: Safe.optional(Safe.int),
});

/**
 * Structure visible to public (member)-facing site.
 */
const publicAction = Safe.obj({
  guid: Safe.str,
  journey_guid: Safe.str,
  type: Safe.nullable(Safe.str),
  branches: Safe.array(branchCondition),
});

const journey: Safe.Type<Journey> = Safe.obj({
  guid: Safe.str,
  slug: Safe.str,
  name: Safe.str,
  internal_name: Safe.optional(Safe.str),
  created_at: Safe.dateIso,
  updated_at: Safe.dateIso,
  went_live_at: Safe.optional(Safe.dateUnixMillis),
  actions: Safe.array(action),
  og_image: Safe.str,
  og_description: Safe.str,
  og_title: Safe.str,
  initial_action_guid: Safe.optional(Safe.uuid),
  created_by: Safe.str,
  show_journey_title: Safe.bool,
  show_on_public_lists: Safe.bool,
  campaigns: Safe.optional(CampaignProviderConfigSerializer),
});

const journeyListItem = Safe.obj({
  guid: Safe.str,
  slug: Safe.str,
  name: Safe.str,
  internal_name: Safe.optional(Safe.str),
  created_at: Safe.dateUnixMillis,
  updated_at: Safe.dateUnixMillis,
  went_live_at: Safe.optional(Safe.dateUnixMillis),
  og_image: Safe.str,
  og_description: Safe.str,
  og_title: Safe.str,
  successful_actions: Safe.int,
  donations: Safe.array(donationReport),
  initial_action_guid: Safe.optional(Safe.uuid),
  initial_action_type: Safe.optional(Safe.nullable(Safe.str)),
  created_by: Safe.str,
});

/**
 * A journey list item definition that we don't mind unauthenticated users seeing.
 */
const journeyListPublicItem = Safe.obj({
  guid: Safe.str,
  slug: Safe.str,
  url: Safe.str,
  name: Safe.str,
  went_live_at: Safe.optional(Safe.dateUnixMillis),
  og_image: Safe.str,
  og_description: Safe.str,
  og_title: Safe.str,
  successful_actions: Safe.int,
  initial_action_type: Safe.optional(Safe.nullable(Safe.str)),
  actions_target: Safe.int,
  donations: Safe.array(
    Safe.obj({
      currency: Safe.str,
      amount: Safe.float,
    })
  ),
});

const emailAddress = Safe.obj({
  name: Safe.optional(Safe.str),
  email: Safe.str,
});

const crmSurveyResponse = Safe.variant(
  "boolean",
  Safe.obj({
    external_question_id: Safe.optional(Safe.str),
    question: Safe.str,
    answer: Safe.bool,
  }),
  "text",
  Safe.obj({
    external_question_id: Safe.optional(Safe.str),
    question: Safe.str,
    answer: Safe.str,
  }),
  "select",
  Safe.obj({
    external_question_id: Safe.optional(Safe.str),
    question: Safe.str,
    options: Safe.array(Safe.str),
    answer: Safe.optional(Safe.str),
  }),
  "multiselect",
  Safe.obj({
    external_question_id: Safe.optional(Safe.str),
    question: Safe.str,
    options: Safe.array(Safe.str),
    answer: Safe.array(Safe.str),
  }),
  "ranking",
  Safe.obj({
    external_question_id: Safe.optional(Safe.str),
    question: Safe.str,
    options: Safe.array(Safe.str),
    answer: Safe.array(Safe.str),
  })
);

// Upon reading and writing this data wrapped in a 'metadata' object:
//   e.g. Donation{ ..., metadata: { boost_amount: 0.1 }, ...}
//
// NB: This is handled in the associated SQL CRUD functions and mapped to the metadata_boost_ammout'
//     columns in the `donate__attempt` and `donation` tables.
export const donationMetadataSerializer = Safe.obj({ boost_amount: Safe.float });

const crmDonation = Safe.obj({
  is_regular: Safe.bool,
  amount: Safe.float,
  currency: Safe.str,
  metadata: donationMetadataSerializer,
  payment_processor: Safe.str,
  external_transaction_id: Safe.str,
  external_customer_ref: Safe.optional(Safe.str),
  description: Safe.str,
  tax_deductible: Safe.optional(Safe.bool),
  // 'success' is the only possible status for now
  status: Safe.oneOf("success"),
});

const donationSummarySerializer = Safe.obj({
  numMonthly: Safe.int,
  sumMonthly: Safe.float,
  numOneOff: Safe.int,
  sumOneOff: Safe.float,
  numBoosts: Safe.int,
  sumBoosts: Safe.float,
  avgDonation: Safe.float,
  sumDonations: Safe.float,
});

export type DonationSummary = Safe.TypeIn<typeof donationSummarySerializer>;

/**
 * Represents the number of people who STARTed an action vs the number of people who ENDed an action.
 * 'completed' would be a better term :/
 * The actions over all stats are represented by a record with a variantIdx of undefined.
 * The action _variants_ are represented by a record with a variantIdx of 0..n
 * donationReports: Represents the aggregated donation stats across all donations regardless of
 *                  frequecy types.
 */
const journeyActionStats = Safe.obj({
  actionGuid: Safe.str,
  variantIdx: Safe.optional(Safe.int),
  start: Safe.int,
  end: Safe.int,
  percentSuccess: Safe.float,
  donationSummary: Safe.optional(donationSummarySerializer),
});

const publicPrefillData = Safe.obj({
  name: Safe.optional(Safe.str),
  has_email: Safe.bool,
  has_postcode: Safe.bool,
});

const samPrefillData = Safe.obj({
  enabled: Safe.bool,
  subject: Safe.str,
  body: Safe.str,
});

const publicJourneyStep = Safe.obj({
  action: publicAction,
  variant_idx: Safe.int,
  custom_data: Safe.raw,
  redacted_prefill: Safe.optional(publicPrefillData),
  consents: Safe.optional(Safe.array(consent)),
  isStaff: Safe.bool,
  takenAction: Safe.bool,
  sam_prefill: Safe.optional(samPrefillData),
  show_journey_title: Safe.optional(Safe.bool),
});

const publicJourneyStepResponse = Safe.variant(
  "journey_ended",
  Safe.obj({
    journey_name: Safe.str,
  }),
  "step",
  Safe.obj({
    isEarlyAccessStaffUser: Safe.bool,
    journey_name: Safe.str,
    step: publicJourneyStep,
  })
);

const user = Safe.obj({
  guid: Safe.uuid,
  name: Safe.str,
  api_key: Safe.optional(Safe.str),
  email: Safe.optional(Safe.str),
  created_at: Safe.dateIso,
  updated_at: Safe.dateIso,
});

const role = Safe.obj({
  guid: Safe.uuid,
  name: Safe.str,
  permissions: Safe.array(Safe.str),
});

export const filestoreStats = Safe.obj({
  state_available: Safe.int,
  state_erasing: Safe.int,
  state_erased: Safe.int,
  files_slow_to_erase: Safe.nullable(Safe.array(Safe.str)),
});

export const filestoreFile = Safe.obj({
  guid: Safe.uuid,
  url: Safe.str,
  status: Safe.str,
  expire_at: Safe.optional(Safe.dateIso),
  personal_data_guid: Safe.optional(Safe.uuid),
});

export const journeyTemplate = Safe.obj({
  name: Safe.str,
  description: Safe.str,
  journey_guid: Safe.uuid,
});

export const journeyTemplates = Safe.array(journeyTemplate);

export const journeyContinuation = Safe.obj({
  og_image: Safe.str,
  name: Safe.str,
  slug: Safe.str,
  signature_count: Safe.int,
});

/**
 * The flow core supports recording donations against actions, but implementation
 * of payment taking is left to plugins. This allows good donation reporting in
 * core, while leaving payment gateway and action UI responsiblity in plugins
 */
const donation = Safe.obj({
  guid: Safe.uuid,
  journey_guid: Safe.uuid,
  action_guid: Safe.uuid,
  variant_idx: Safe.int,
  currency: Safe.str,
  amount: Safe.float,
  metadata: donationMetadataSerializer,
  frequency: donationFrequency,
  created_at: Safe.dateIso,
});

export const Serializers = {
  action,
  branchCondition,
  campaignTarget,
  campaign,
  consent,
  consentConfig,
  consentInput,
  consentLevel,
  consentMethod,
  consentText,
  filestoreStats,
  filestoreFile,
  journey,
  journeyActionStats,
  journeyContext,
  journeyContinuation,
  journeyListItem,
  journeyListPublicItem,
  journeyTemplate,
  journeyTemplates,
  member,
  personalDataOwner,
  publicPrefillData,
  publicAction,
  publicCampaignTarget,
  publicJourneyStep,
  publicJourneyStepResponse,
  role,
  crmSurveyResponse,
  crmDonation,
  user,
  donation,
  donationFrequency,
  donationReport,
  emailAddress,
};
